import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';

export interface FilterChip {
  key: string;
  label: string;
}

/**
 * Search box + extra filter controls + removable chips for what is applied + "Clear all".
 * Lifted from the shopfloor filter (LB Req 5 A.5).
 *
 *   <lb-filter-bar [(search)]="searchText" searchPlaceholder="Search orders..." [chips]="activeChips"
 *                  (removeChip)="clearFilter($event)" (clearAll)="clearFilters()">
 *     <select lbFilter class="form-select">...</select>
 *   </lb-filter-bar>
 */
@Component({
  selector: 'lb-filter-bar',
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="lb-filter-bar" role="search">
      <div class="lb-filter-search">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input #input type="search" class="form-control" [placeholder]="searchPlaceholder" [attr.aria-label]="searchLabel || searchPlaceholder"
               [ngModel]="search" (ngModelChange)="onSearch($event)" autocomplete="off">
        <button *ngIf="search" type="button" class="btn btn-link lb-filter-search-clear" aria-label="Clear search" (click)="onSearch(''); input.focus()">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <ng-content select="[lbFilter]"></ng-content>
      <div class="lb-filter-end"><ng-content></ng-content></div>
    </div>
    <div class="lb-filter-chips" *ngIf="chips.length > 0">
      <span class="lb-filter-chip" *ngFor="let chip of chips">
        {{ chip.label }}
        <button type="button" class="btn-close" [attr.aria-label]="'Remove filter ' + chip.label" (click)="removeChip.emit(chip.key)"></button>
      </span>
      <button type="button" class="btn btn-link btn-sm lb-filter-clear-all" (click)="clearAll.emit()">Clear all</button>
    </div>
  `,
  styles: [`
    .lb-filter-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--lb-space-2);
    }
    .lb-filter-bar > .form-select,
    .lb-filter-bar > .form-control {
      width: auto;
      max-width: 100%;
    }
    .lb-filter-search {
      position: relative;
      flex: 1 1 16rem;
      max-width: 26rem;
    }
    .lb-filter-search .fa-search {
      position: absolute;
      left: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--lb-text-muted);
      pointer-events: none;
    }
    .lb-filter-search .form-control {
      padding-left: 2.2rem;
      padding-right: 2.2rem;
    }
    .lb-filter-search .form-control::-webkit-search-cancel-button {
      display: none;
    }
    .lb-filter-search-clear {
      position: absolute;
      right: 0.25rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--lb-text-muted);
      padding: 0.25rem 0.5rem;
    }
    .lb-filter-end {
      margin-left: auto;
      display: flex;
      flex-wrap: wrap;
      gap: var(--lb-space-2);
    }
    .lb-filter-end:empty {
      display: none;
    }
    .lb-filter-chips {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--lb-space-2);
      margin-top: var(--lb-space-2);
    }
    .lb-filter-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      background: var(--lb-primary-soft);
      color: var(--lb-primary-strong);
      border-radius: 999px;
      padding: 0.2rem 0.4rem 0.2rem 0.75rem;
      font-size: 0.875rem;
    }
    .lb-filter-chip .btn-close {
      font-size: 0.6rem;
      padding: 0.35rem;
    }
    @media (max-width: 575.98px) {
      .lb-filter-search {
        max-width: none;
        flex-basis: 100%;
      }
      .lb-filter-bar > .form-select,
      .lb-filter-bar > .form-control {
        flex: 1 1 45%;
      }
    }
  `]
})
export class LbFilterBarComponent {
  @Input() search = '';
  @Output() searchChange = new EventEmitter<string>();
  @Input() searchPlaceholder = 'Search...';
  @Input() searchLabel = '';
  @Input() chips: FilterChip[] = [];
  @Output() removeChip = new EventEmitter<string>();
  @Output() clearAll = new EventEmitter<void>();

  @ViewChild('input') input!: ElementRef<HTMLInputElement>;

  onSearch(value: string): void {
    this.search = value;
    this.searchChange.emit(value);
  }
}
